/** Measurement policy. Coordinates and insets are CSS pixels in the browser viewport. */
export type ControlAnchorOptions = {
  /** "hide" rejects clipped controls; "pin" clamps Y; "pin-both" clamps X/Y; "follow" keeps the real centre. */
  scrollBehavior?: "hide" | "pin" | "pin-both" | "follow";
  /** Inward distance from pinned clip edges, limited to half the visible dimension. */
  edgeInset?: number;
};

/** Keeps offscreen drawing anchors inside the visible plan without clipping routes to other controls. */
export const DRAWING_ANCHOR_OPTIONS: Readonly<ControlAnchorOptions> = Object.freeze({ scrollBehavior: "pin-both", edgeInset: 6 });

/** Measures the visible scrollport without including its border or scrollbars. */
export function scrollportBounds(element: Element): { left: number; right: number; top: number; bottom: number } {
  const rect = element.getBoundingClientRect();
  const html = element as HTMLElement;
  if (!(html.offsetWidth > 0 && html.offsetHeight > 0)) return rect;
  const scaleX = (rect.right - rect.left) / html.offsetWidth;
  const scaleY = (rect.bottom - rect.top) / html.offsetHeight;
  const left = rect.left + html.clientLeft * scaleX;
  const top = rect.top + html.clientTop * scaleY;
  return { left, top, right: left + html.clientWidth * scaleX, bottom: top + html.clientHeight * scaleY };
}

/** Clips control anchors to scroll ancestors, optionally pinning scrolled rows to their vertical edges. */
export function visibleControlPoint(
  element: Element,
  options: Readonly<ControlAnchorOptions> = {},
): { clientX: number; clientY: number } | undefined {
  const pinBoth = options.scrollBehavior === "pin-both";
  const pinVertical = options.scrollBehavior === "pin" || pinBoth;
  const followPosition = options.scrollBehavior === "follow";
  if (!element.isConnected || !element.getClientRects().length) return;
  const rect = element.getBoundingClientRect();
  if (rect.right <= rect.left || rect.bottom <= rect.top) return;
  let left = pinBoth ? 0 : Math.max(0, rect.left),
    right = pinBoth ? window.innerWidth : Math.min(window.innerWidth, rect.right);
  let top = pinVertical ? 0 : Math.max(0, rect.top),
    bottom = pinVertical ? window.innerHeight : Math.min(window.innerHeight, rect.bottom);
  for (let parent: Element | null = element; parent; parent = parent.parentElement) {
    const style = getComputedStyle(parent);
    if (style.visibility === "hidden" || style.display === "none" || style.opacity === "0") return;
    if (parent === element || followPosition) continue;
    const bounds = pinBoth ? scrollportBounds(parent) : parent.getBoundingClientRect();
    if (/(auto|scroll|hidden|clip)/.test(style.overflowX)) {
      left = Math.max(left, bounds.left);
      right = Math.min(right, bounds.right);
    }
    if (/(auto|scroll|hidden|clip)/.test(style.overflowY)) {
      top = Math.max(top, bounds.top);
      bottom = Math.min(bottom, bounds.bottom);
    }
  }
  if (followPosition) return { clientX: (rect.left + rect.right) / 2, clientY: (rect.top + rect.bottom) / 2 };
  if (right <= left || bottom <= top) return;
  const requestedInset = options.edgeInset ?? 0;
  const inset = Number.isFinite(requestedInset) ? Math.max(0, Math.min(requestedInset, (bottom - top) / 2)) : 0;
  const clientY = pinVertical
    ? Math.max(top + inset, Math.min(bottom - inset, (rect.top + rect.bottom) / 2))
    : (top + bottom) / 2;
  const horizontalInset = Number.isFinite(requestedInset) ? Math.max(0, Math.min(requestedInset, (right - left) / 2)) : 0;
  const clientX = pinBoth
    ? Math.max(left + horizontalInset, Math.min(right - horizontalInset, (rect.left + rect.right) / 2))
    : (left + right) / 2;
  return { clientX, clientY };
}

/** Keeps existing key connections in their editor when vertical scrolling hides the key. */
export function buttonEditorScrollAnchor(element: Element): { clientX: number; clientY: number } | undefined {
  // A hidden channel/page or closed editor must not produce a substitute anchor.
  if (!visibleControlPoint(element, { scrollBehavior: "follow" })) return;
  const editor = element.closest(".C1_buttonEditorViewport, .C1_dockSectionBody, .C1_dialogContent");
  if (!editor || !visibleControlPoint(editor)) return;
  const port = scrollportBounds(editor);
  let left = Math.max(0, port.left), right = Math.min(window.innerWidth, port.right);
  let top = Math.max(0, port.top), bottom = Math.min(window.innerHeight, port.bottom);
  for (let parent = editor.parentElement; parent; parent = parent.parentElement) {
    const style = getComputedStyle(parent);
    const clip = scrollportBounds(parent);
    if (/(auto|scroll|hidden|clip)/.test(style.overflowX)) {
      left = Math.max(left, clip.left); right = Math.min(right, clip.right);
    }
    if (/(auto|scroll|hidden|clip)/.test(style.overflowY)) {
      top = Math.max(top, clip.top); bottom = Math.min(bottom, clip.bottom);
    }
  }
  if (right <= left || bottom <= top) return;
  const rect = element.getBoundingClientRect();
  if (editor.classList?.contains("C1_buttonEditorViewport")) {
    return {
      clientX: Math.max(left + 6, Math.min(right - 6, (rect.left + rect.right) / 2)),
      clientY: Math.max(top + 6, Math.min(bottom - 6, (rect.top + rect.bottom) / 2)),
    };
  }
  if (rect.bottom > top && rect.top < bottom) return;
  const insetX = Math.min(6, (right - left) / 2);
  const insetY = Math.min(6, (bottom - top) / 2);
  return { clientX: left + insetX, clientY: rect.bottom <= top ? top + insetY : bottom - insetY };
}
